/**
 * Queue rows. A row is one tap target that opens the detail; there is no approve control
 * on any list (spec 3.11 and rule 2).
 */

import type { Proposal, TwinRequest } from '../api/types.js';
import { Link } from '../app/router.js';
import { AGENT_NAMES, AGENT_SHAPES } from '../app/selectors.js';
import { formatAge } from '../lib/format.js';
import { AgentGlyph, Icon } from './icons.js';
import { ResultMark, TierStack, TypeChip } from './primitives.js';

export function ApprovalRow({
  proposal,
  now,
  desktop = false,
}: {
  proposal: Proposal;
  now: number;
  desktop?: boolean;
}) {
  const t4 = proposal.tier === 'T4';
  return (
    <Link to={`/onay/${proposal.id}`} className={desktop ? 'arow arow--desktop' : 'arow'}>
      <span className="arow__tier">
        <TierStack tier={proposal.tier} topic={proposal.topic} />
      </span>
      <span className="arow__title">{proposal.title}</span>
      <span className="arow__age mono">{formatAge(now - proposal.queuedAt)}</span>
      <span className="arow__summary">
        {proposal.summaryPrefix}
        {proposal.summaryLang === 'en' ? <span lang="en">{proposal.summary}</span> : proposal.summary}
        {desktop && t4 && proposal.kind === 'coupon' ? ` · tavan %${proposal.policy.capPct}` : null}
      </span>
      <span className="arow__chevron">
        <Icon name="chevron-right" size={18} />
      </span>
      <span className="arow__meta">
        <AgentGlyph shape={AGENT_SHAPES[proposal.agent]} />
        {AGENT_NAMES[proposal.agent]}
        <span aria-hidden="true">·</span>
        <ResultMark verdict={proposal.verifier.verdict} />
        {proposal.waiting ? (
          <>
            <span aria-hidden="true">·</span>
            {proposal.waiting}
          </>
        ) : null}
        {desktop && proposal.desktopNote ? (
          <>
            <span aria-hidden="true">·</span>
            <span className="ellipsis">{proposal.desktopNote}</span>
          </>
        ) : null}
        {desktop && t4 && proposal.kind === 'coupon' ? (
          <>
            <span aria-hidden="true">·</span>
            <span className="accent-ink">{proposal.impact.replace(/\.$/, '')}</span>
          </>
        ) : null}
      </span>
    </Link>
  );
}

/** The sub-band above T4 rows: the decision is Sercan's, and there is no quick approval. */
export function T4Band({ desktop = false }: { desktop?: boolean }) {
  return (
    <div className={desktop ? 't4band t4band--desktop' : 't4band'}>
      <span className="key">Karar senin</span>
      <span className="t4band__note">
        <Icon name="lock" size={12} stroke={2} />
        {desktop ? 'hızlı onay kapalı · ayrıntıda basılı tut' : 'hızlı onay kapalı'}
      </span>
    </div>
  );
}

export function requestMeta(request: TwinRequest): string | null {
  if (request.customerWaiting) return 'müşteri bekliyor';
  if (request.type === 'design') return request.origin;
  return null;
}

/** Compact request row for Bugün (48 high). */
export function RequestRow({ request, now }: { request: TwinRequest; now: number }) {
  const meta = requestMeta(request);
  return (
    <Link to={`/istek/${request.id}`} className="rrow">
      <TypeChip type={request.type} variant="short" />
      <span className="rrow__body">
        <span className="rrow__title">{request.title}</span>
        <span className="rrow__meta">
          <AgentGlyph shape={AGENT_SHAPES[request.agent]} />
          {AGENT_NAMES[request.agent]}
          {meta ? (
            <>
              <span aria-hidden="true">·</span>
              {meta}
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <span className="mono">{formatAge(now - request.createdAt)}</span>
        </span>
      </span>
      <Icon name="chevron-right" size={18} className="rrow__chevron" />
    </Link>
  );
}
